import React from 'react';

import { Modal, Button, Table, TableSelection, TablePagination, Flashbar, Input, Alert } from 'AWS-UI-Components-React';

import { areFieldValuesEquivalent } from '../channel_mapping/channel_mapping_utils';
import { ERROR_MESSAGE } from '../../../constants/views';

const NO_CHANGE_MESSAGE = '<No changes>';
const tablePageSize = 10;

export class CMReviewChanges extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedChannelMappings: undefined,
    };
  }

  getColumnDefinitions = () => {
    return [
      {
        id: 'cognosChannelType',
        header: 'Cognos Channel Type',
        cell: (item) => item.cognosChannelType,
      },
      {
        id: 'cognosChannel',
        header: 'Cognos Channel',
        cell: (item) => item.cognosChannel,
      },
      {
        id: 'hcpChannelType',
        header: 'HCP Channel Type',
        cell: (item) => this.getDiffItemDisplay(item, 'hcpChannelType'),
      },
      {
        id: 'hcpMerchantChannel',
        header: 'HCP Merchant Channel',
        cell: (item) => this.getDiffItemDisplay(item, 'hcpMerchantChannel'),
      },
      {
        id: 'excluded',
        header: 'Cognos Excluded',
        cell: (item) => this.getDiffItemDisplay(item, 'excluded'),
      },
      {
        id: 'hcpExcluded',
        header: 'HCP Excluded',
        cell: (item) => this.getDiffItemDisplay(item, 'hcpExcluded'),
      },
      {
        id: 'ignored',
        header: 'Ignored',
        cell: (item) => this.getDiffItemDisplay(item, 'ignored'),
      },
    ];
  };

  getDiffItemDisplay = (item, field) => {
    if (item[field].noChange) {
      return <i>{NO_CHANGE_MESSAGE}</i>;
    }
    return (
      <div>
        <div>{Array.isArray(item[field].updated) ? item[field].updated.join(', ') : item[field].updated}</div>
        <div>
          <del>{Array.isArray(item[field].removed) ? item[field].removed.join(', ') : item[field].removed}</del>
        </div>
      </div>
    );
  };

  getDiffItems = (stagedChannelMappings, modifiedChannelMappingsById, originalChannelMappings) => {
    stagedChannelMappings = stagedChannelMappings ?
                            Object.values(stagedChannelMappings) :
                            Object.values(modifiedChannelMappingsById);
    if (!stagedChannelMappings.length) {
      return [];
    }

    return stagedChannelMappings.map((modifiedChannelMapping) => {
      let originalMapping = originalChannelMappings[modifiedChannelMapping.id];
      return {
        cognosChannelType: modifiedChannelMapping.cognosChannelType,
        cognosChannel: modifiedChannelMapping.cognosChannel,
        hcpChannelType: this.getHcpChannelTypeDiff(
          originalMapping === undefined ? "" : originalMapping.hcpChannelType,
          modifiedChannelMapping.hcpChannelType
        ),
        hcpMerchantChannel: this.getMerchantChannelDiff(
          originalMapping === undefined ? "" : originalMapping.hcpMerchantChannel,
          modifiedChannelMapping.hcpMerchantChannel,
        ),
        excluded: this.getCognosExcludedDiff(
          originalMapping === undefined ? "" : originalMapping.excluded,
          modifiedChannelMapping.excluded
        ),
        hcpExcluded: this.getHCPExcludedDiff(
          originalMapping === undefined ? "" : originalMapping.hcpExcluded,
          modifiedChannelMapping.hcpExcluded
        ),
        ignored: this.getIgnoredDiff(
          originalMapping === undefined ? "" : originalMapping.ignored,
          modifiedChannelMapping.ignored
        ),
        id: modifiedChannelMapping.cognosChannelType + "#" + modifiedChannelMapping.hcpChannelType + "#*",
        mappingAdded: originalMapping === undefined,
      };
    });
  };

  getMerchantChannelDiff = (originalMerchantChannel, modifiedMerchantChannel) => {
    if (areFieldValuesEquivalent(originalMerchantChannel, modifiedMerchantChannel)) {
      return { noChange: true };
    }

    const updated = modifiedMerchantChannel ? `${modifiedMerchantChannel}` : 'None';
    const removed = originalMerchantChannel ? `${originalMerchantChannel}` : 'None';
    return { updated, removed };
  };

  getHcpChannelTypeDiff = (originalHcpChannelType, modifiedHcpChannelType) => {
    if (areFieldValuesEquivalent(originalHcpChannelType, modifiedHcpChannelType)) {
      return { noChange: true };
    }

    const updated = modifiedHcpChannelType ? `${modifiedHcpChannelType}` : 'None';
    const removed = originalHcpChannelType ? `${originalHcpChannelType}` : 'None';
    return { updated, removed };
  };

  getCognosExcludedDiff = (originalCognosExcluded, modifiedCognosExcluded) => {
    if (areFieldValuesEquivalent(originalCognosExcluded, modifiedCognosExcluded)) {
      return { noChange: true };
    }

    const updated = Boolean(modifiedCognosExcluded) ? 'Yes' : 'No';
    const removed = Boolean(originalCognosExcluded) ? 'Yes' : 'No';
    return { updated, removed };
  };

  getHCPExcludedDiff = (originalHCPExcluded, modifiedHCPExcluded) => {
    if (areFieldValuesEquivalent(originalHCPExcluded, modifiedHCPExcluded)) {
      return { noChange: true };
    }

    const updated = Boolean(modifiedHCPExcluded) ? 'Yes' : 'No';
    const removed = Boolean(originalHCPExcluded) ? 'Yes' : 'No';
    return { updated, removed };
  };

  getIgnoredDiff = (originalIgnored, modifiedIgnored) => {
    if (areFieldValuesEquivalent(originalIgnored, modifiedIgnored)) {
      return { noChange: true };
    }

    const updated = Boolean(modifiedIgnored) ? 'Yes' : 'No';
    const removed = Boolean(originalIgnored) ? 'Yes' : 'No';
    return { updated, removed };
  };

  onSelectionChange = (event) => {
    const selectedChannelMappings = event.detail.selectedItems.map((item) => item.id);
    this.setState({
      selectedChannelMappings,
    });
  };

  getSelectedCount = (selectedItems) => {
    return selectedItems && selectedItems.length ? <span>({selectedItems.length})</span> : null;
  };

  unstageSelected = (selectedItems) => {
    selectedItems = selectedItems.reduce((obj, item) => ({...obj, [item['id']]: item}), {});
    const { originalChannelMappings, modifiedChannelMappingsById } = this.props;
    const stagedChannelMappings = this.props.stagedChannelMappings ?
                            this.props.stagedChannelMappings :
                            modifiedChannelMappingsById;
    const filterSelected = Object.keys(stagedChannelMappings)
                           .filter(key => Object.keys(selectedItems).includes(key))
                           .reduce((obj, key) => {
                             obj[key] = stagedChannelMappings[key];
                             return obj;
                           }, {});
    if (selectedItems) {
      this.props.unstageChannelMappingChanges(filterSelected, originalChannelMappings);
    }

    this.setState({ selectedChannelMappings: [] });
  };

  publishSelected = (selectedItems) => {
    const selectedChannelMappings = selectedItems.reduce((obj, item) => ({...obj, [item['id']]: item}), {});
    const { latestChannelMappingsById, modifiedChannelMappingsById } = this.props;
    const stagedChannelMappings = this.props.stagedChannelMappings ?
                                  this.props.stagedChannelMappings :
                                  modifiedChannelMappingsById;
    const filterSelected = Object.keys(stagedChannelMappings)
                                 .filter(key => Object.keys(selectedChannelMappings).includes(key))
                                 .reduce((obj, key) => {
                                   obj[key] = stagedChannelMappings[key];
                                   return obj;
                                 }, {});
    for (let id of Object.keys(filterSelected)) {
      filterSelected[id].comment = this.state.comment ? this.state.comment : this.props.comment;
    }

    if (selectedChannelMappings) {
      this.props.publishChannelMappingChanges(filterSelected, latestChannelMappingsById);
    }

    this.setState({ selectedChannelMappings: [] });
  };

  onDismiss = () => {
    this.props.onDismiss();

    this.setState({ selectedChannelMappings: undefined });
  };

  onUpdateComment = (event) => {
    this.setState({ comment: event.detail.value });
  };

  render() {
    const {
      visible,
      comment,
      stagedChannelMappings,
      originalChannelMappings,
      modifiedChannelMappingsById,
      expandToFit = true,
      size = 'large',
      errorScreenMessage,
      successScreenMessage
    } = this.props;

    const { selectedChannelMappings } = this.state;

    const diffItems = this.getDiffItems(stagedChannelMappings, modifiedChannelMappingsById, originalChannelMappings);

    const selectedItems =
      selectedChannelMappings === undefined
        ? diffItems
        : diffItems.filter((item) => selectedChannelMappings.includes(item.id));

    const content = (
      <div>
        <div className="awsui-util-pt-xs awsui-util-mb-s">
          <Flashbar
            items={
              (selectedItems && selectedItems.find((mapping) => mapping.mappingAdded === true ))
                ? [
                    {
                      type: 'info',
                      content: `A new mapping will be added`,
                    },
                  ]
                : []
        }
          />
        </div>
        <Table
          columnDefinitions={this.getColumnDefinitions()}
          items={diffItems}
          empty={'No staged changes.'}
          stickyHeader
        >
          <TableSelection onSelectionChange={this.onSelectionChange} trackBy="id" selectedItems={selectedItems} />
          <TablePagination pageSize={tablePageSize} />
        </Table>
        <h3>Comments:</h3>
        <div>
          <Input value={comment} onChange={this.onUpdateComment}/>
        </div>
      </div>
    );

    return (
      <div>
        <Modal
          visible={visible}
          header={'Review/Publish Changes'}
          onDismiss={this.onDismiss}
          expandToFit={expandToFit}
          size={size}
          footer={
            <span className="awsui-util-f-r">
              <Button
                key={'UNSTAGE_SELECTED_BUTTON'}
                variant={'normal'}
                disabled={!this.getSelectedCount(selectedItems)}
                onClick={() => this.unstageSelected(selectedItems)}
              >
                Revert Selected {this.getSelectedCount(selectedItems)}
              </Button>
              <Button
                key={'PUBLISH_SELECTED_BUTTON'}
                variant={'primary'}
                disabled={!this.getSelectedCount(selectedItems)}
                onClick={() => this.publishSelected(selectedItems)}
              >
                Publish Selected {this.getSelectedCount(selectedItems)}
              </Button>
            </span>
          }
        >
          <div>
            <div>
              <Flashbar
                items={
                  errorScreenMessage
                    ? [
                        {
                          type: 'error',
                          content: `${ERROR_MESSAGE}${errorScreenMessage}`,
                          onDismiss: () => this.setState({ errorScreenMessage: null }),
                        },
                      ]
                    : []
                }
              />
            </div>
            <div>
              <Flashbar
                items={
                  successScreenMessage
                  ? [
                      {
                        type: 'success',
                        dismissible: true,
                        content: `${successScreenMessage}`,
                        onDismiss: () => this.setState({ successScreenMessage: null }),
                      },
                    ]
                  : []
                }
              />
            </div>
            {content}
            <div style={{ textAlign: 'center', fontStyle: 'italic' }}>
              <p>Impact of published changes on data will be visible after the next data refresh.</p>
            </div>
          </div>
        </Modal>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/config_central/channel_mapping/cm_review_changes.jsx